import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal } from '@angular/core';
import { catchError, Observable, of, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { GetFile } from '../../interfaces/file';
import { AlarmService } from '../alarm.service';
import { GetUserByIdResponse, UserService } from '../user.service';
import { EmergencyAlerts } from '../../interfaces/emergency-alerts';
import { EmergencyAlertsService } from '../emergency-alerts.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  private userService = inject(UserService);
  private http = inject(HttpClient);
  private alarmService = inject(AlarmService);
  private emergencyAlertService = inject(EmergencyAlertsService);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control':
        'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
    }),
  };

  private apiUrl = `${environment.api}`;
  private hubUrl = environment.chatHub;
  private muteNotificationSound = signal<boolean>(false);

  activeUser = signal<any>(null);
  previousChats = signal<PreviousChat[]>([]);
  pendingChats = signal<GetUserByIdResponse[]>([]);
  callSenderId = signal<number>(0);
  messages = signal<any[]>([]);

  newSessionsStarted: Signal<number> = computed(
    () => this.previousChats().filter((chat) => chat.isNew).length
  );

  private subscriptions: Subscription[] = [];
  private hubConnection!: HubConnection;
  hasSession: string = '';

  constructor() {}

  startConnection(token: string) {
    if (
      this.hubConnection?.state !== HubConnectionState.Disconnected &&
      this.hubConnection
    ) {
      return;
    }

    this.hubConnection = new HubConnectionBuilder()
      .withUrl(`${this.hubUrl}?access_token=${token}`)
      .configureLogging(LogLevel.Debug)
      .withAutomaticReconnect()
      .build();

    this.hubConnection
      .start()
      .catch((err) =>
        console.error('Error starting SignalR connection: ', err)
      );

    this.registerHubListeners();

    this.subscriptions.push(
      this.getAllChats().subscribe((chats) => {
        this.updatePreviousChats(chats);
      })
    );
  }
  setCallSenderId(userId: number) {
    this.callSenderId.set(userId);
  }

  private registerHubListeners() {
    this.hubConnection.on(
      'ReceiveMessage',
      (receivedMessage: ReceiveMessage) => {
        this.alarmService.playNotification(
          '../../assets/notification_sound.mp3'
        );
        this.messages.update((messages) => [...messages, receivedMessage]);
        this.moveChatToTop(
          Number(receivedMessage.senderId),
          receivedMessage.content
        );
      }
    );

    this.hubConnection.on('MessageSeen', (message) => {
      this.messages.set(
        this.messages().map((mes) =>
          mes.messageId === message.messageId ? { ...mes, seen: true } : mes
        )
      );
    });

    this.hubConnection.on(
      'SessionStarted',
      (notification: SessionStartedMessage) => {
        this.handleSessionStarted(notification);
      }
    );

    this.hubConnection.on('NotifyUserLeft', (notification) => {
      this.messages.update((value) => [
        ...value,
        {
          message:
            'The user has left the chat. Any messages sent will appear as a notification.',
          sessionId: notification.sessionId,
          userId: notification.userId,
        },
      ]);
    });
    this.hubConnection.on(
      'ReceiveEmergencyAlert',
      (emergencyAlert: EmergencyAlerts) => {
        this.handleEmergencyAlert(emergencyAlert);
      }
    );
    this.hubConnection.on('ReceiveFile', (file: GetFile) => {
      this.handleNewFiles(file);
    });
  }
  private handleNewFiles(newFiles: GetFile): void {
    this.emergencyAlertService.setFiles(newFiles);
    this.alarmService.setFilesCounter();
  }
  private handleEmergencyAlert(emergencyAlert: EmergencyAlerts) {
    this.emergencyAlertService.setAlerts(emergencyAlert);
    this.alarmService.setEmergencyAlertsCounter();
  }
  private handleSessionStarted(notification: SessionStartedMessage) {
    this.userService.getUserById(Number(notification.senderId)).subscribe({
      next: (user) => {
        const updatedUser = {
          ...user,
          content: notification.content,
          sentAt: notification.sentAt,
          isNew: true,
        };
        this.previousChats.update((chats) => {
          const existingIndex = chats.findIndex(
            (chat) => Number(chat.senderId) === Number(notification.senderId)
          );

          let updatedChats;
          if (existingIndex !== -1) {
            updatedChats = [...chats];
            updatedChats.splice(existingIndex, 1);
          } else {
            updatedChats = [...chats];
          }

          updatedChats = [updatedUser, ...updatedChats];

          return this.sortChats(updatedChats);
        });

        if (!this.muteNotificationSound()) {
          this.alarmService.playNotification(
            '../../assets/notification_sound.mp3'
          );
        }
      },
      error: (err) => console.error('Failed to fetch user:', err),
    });
  }
  private updatePreviousChats(chats: any[]) {
    const uniqueChatsByDate = new Map<number, any>();

    chats.forEach((chat) => {
      const existingChat = uniqueChatsByDate.get(chat.senderId);
      if (
        !existingChat ||
        new Date(chat.sentAt) > new Date(existingChat.sentAt)
      ) {
        uniqueChatsByDate.set(chat.senderId, { ...chat, isNew: false });
      }
    });

    const filteredChats = Array.from(uniqueChatsByDate.values());
    this.previousChats.set(this.sortChats(filteredChats));
  }

  private moveChatToTop(senderId: number, latestMessage: string) {
    this.previousChats.update((chats) => {
      const index = chats.findIndex(
        (chat) => Number(chat.senderId) === Number(senderId)
      );
      if (index === -1) return chats;

      const updatedChat = {
        ...chats[index],
        message: latestMessage,
        isNew: true,
      };
      const updatedChats = [...chats];
      updatedChats.splice(index, 1);

      return this.sortChats([updatedChat, ...updatedChats]);
    });
  }

  private sortChats(chats: any[]): any[] {
    return chats.sort((a, b) => {
      if (a.isNew && !b.isNew) return -1;
      if (!a.isNew && b.isNew) return 1;
      return new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();
    });
  }

  async sendMessage(sessionId: string, content: any) {
    try {
      await this.hubConnection.invoke(
        'SendMessage',
        String(sessionId),
        String(this.callSenderId()),
        String(content.content)
      );
      this.messages.update((messages) => [...messages, content]);
    } catch (err) {
      content.content =
        'Failed to send message! Please refresh the browser and try again!';
      this.messages.update((messages) => [...messages, content]);
      console.error('Error while sending message:', err);
    }
  }

  startSession(userId: string, callCenterId: string) {
    this.hubConnection
      .invoke('StartSession', userId, callCenterId)
      .then(() => {
        this.hasSession = `session-${userId}-${callCenterId}`;
      })
      .catch((err) => console.error('Error while starting session:', err));
  }

  leftSession(sessionId: string, userId: string) {
    this.hubConnection
      .invoke('LeftSession', sessionId.toString(), userId.toString())
      .catch((err) => console.error('Error while sending message:', err));
  }

  destroyConnection() {
    this.hubConnection
      ?.stop()
      .catch((err) =>
        console.error('Error while stopping SignalR connection:', err)
      );
  }

  isActiveUser() {
    return this.activeUser() !== null;
  }

  destroyActiveUser() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.activeUser.set(null);
  }

  changeActiveChat(user: any) {
    const { senderId, receiverId } = user;
    const userId = senderId || user.userId || receiverId || user.id;

    this.activeUser.set(user);
    // if (!this.hubConnection) this.startConnection('3');

    this.markChatAsRead(senderId);

    this.startSession(String(userId), String(this.callSenderId()));
    this.subscriptions.push(
      this.getChatMessage(userId).subscribe({
        next: (messages: any) => {
          user.isNew = false;
          this.messages.set(messages);
        },
      })
    );
  }

  private markChatAsRead(senderId: number) {
    this.previousChats.update((chats) =>
      chats.map((chat) =>
        chat.senderId == senderId ? { ...chat, isNew: false } : chat
      )
    );
  }

  getAllChats(): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/Chat/Callcenter-messages/${String(this.callSenderId())}`,
      this.httpOptions
    );
  }

  getChatMessage(otherUserId: number) {
    return this.http
      .get(
        `${this.apiUrl}/Chat/Between/${String(
          this.callSenderId()
        )}/${otherUserId}`
      )
      .pipe(catchError(() => of([])));
  }

  muteUnmuteNotifications() {
    this.muteNotificationSound.set(!this.muteNotificationSound());
  }
}
export interface UserSessionStarted {
  id: number;
  username: string;
  firstname: string;
  lastname: string;
  email: string;
  message: string;
  isNew: boolean;
  isClicked: boolean;
}

export interface ReceiveMessage {
  sessionId: string;
  senderId: string;
  content: string;
  sentAt: string;
  messageId: number;
}
export interface SessionStartedMessage {
  sessionId: string;
  senderId: string;
  content: string;
  sentAt: Date;
}
export interface PreviousChat {
  messageId: number;
  senderId: number;
  senderName: string;
  receiverId: number;
  content: string;
  sentAt: Date;
  seen: boolean;
  isNew: boolean;
  isClicked: boolean;
}
